import { createSelector } from 'reselect'

import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import {
  findPostPurchaseCheckout,
  isBookingProtectionItem,
  isExperienceItem,
  isInsuranceItem,
  isLuxPlusSubscriptionItem,
  isSubscriptionJoinItem,
} from 'lib/checkout/checkoutUtils'
import { isFlightItem } from 'checkout/lib/utils/flights/cart'
import { getExperimentEnabledDecision, getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { isSpoofed } from 'selectors/isSpoofed'

import { getIsMobileAppLikeCheckoutEnabled } from './mobileAppLikeCheckoutSelectors'

/**
 * Deliberately broader than the shared `isStandaloneFlights`, which excludes
 * experiences — standalone flights are offered an experiences upsell mid
 * checkout, so that selector would drop the traveller out of the stepper the
 * moment they accepted it.
 */
function isFlightsLedCartItem(item: App.Checkout.AnyItem): boolean {
  return isFlightItem(item) ||
    isExperienceItem(item) ||
    isInsuranceItem(item) ||
    isBookingProtectionItem(item) ||
    isLuxPlusSubscriptionItem(item) ||
    isSubscriptionJoinItem(item)
}

const getIsFlightsLedCart = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => {
    return items.some(isFlightItem) && items.every(isFlightsLedCartItem)
  },
)

/**
 * Eligibility without the experiment decision, so Optimizely activation can be
 * gated on it and traffic that could never see the variant stays out of the results.
 * Must stay variant agnostic — control needs to activate too or there is nothing
 * to compare against.
 *
 * Desktop only for now: mobile has its own design still to be built, and the
 * mobile app like checkout owns that surface today.
 */
export const getCanActivateDesktopSteppedCheckout = createSelector(
  (state: App.State) => getIsFlightsLedCart(state),
  (state: App.State) => state.system?.device?.deviceType !== 'mobile',
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getIsMobileAppLikeCheckoutEnabled(state),
  (state: App.State) => isSpoofed(state),
  (
    isFlightsLedCart,
    isNotMobileDevice,
    postPurchaseMode,
    isMobileAppLikeCheckoutEnabled,
    spoofed,
  ): boolean => {
    return config.BRAND === 'luxuryescapes' &&
      isFlightsLedCart &&
      isNotMobileDevice &&
      !postPurchaseMode &&
      !isMobileAppLikeCheckoutEnabled &&
      !spoofed
  },
)

export const getIsDesktopSteppedCheckoutEnabled = createSelector(
  (state: App.State) => getCanActivateDesktopSteppedCheckout(state),
  (state: App.State) => getOptimizelyExperimentVariation(state, Experiments.desktopSteppedCheckout.id),
  (canActivate, variation): boolean => canActivate && getExperimentEnabledDecision(variation),
)
